"use client";

import Link from "next/link";
import {
  FC,
  Suspense,
  useEffect,
  useState,
  useRef,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useRedirectIfLoggedIn } from "@auth/client-sdk-react";

import { useTextInput, useToggle } from "@/hooks";
import { PASSCODE_LIFETIME_IN_MINS } from "@/constants/auth";

import Text from "../../base/Text/Text";
import Input from "../../form/Input/Input";
import Column from "../../layout/Column/Column";
import Spacer from "../../layout/Spacer/Spacer";
import Image from "../../base/Image/Image";

import { PasswordLogin } from "./PasswordLogin";
import { PasscodeLogin } from "./PasscodeLogin";

interface ILoginProps {
  redirectAfterLogin: string;
}

export const Login: FC<ILoginProps> = ({ redirectAfterLogin }) => {

  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const postLoginQuery = searchParams.get("postLogin") as string | undefined;

  useRedirectIfLoggedIn(postLoginQuery ?? redirectAfterLogin);

  const [ withPassword, toggleWithPassword ] = useToggle(false);
  const [ disabled, setDisabled ] = useToggle(false);
  const [ email, setEmail ] = useTextInput();

  const [ passcodeSent, setPasscodeSent ] = useState(false);
  const [ codeHasExpired, setCodeHasExpired ] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startPasscodeTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setCodeHasExpired(false);
    const secondsInMinute = 60;
    const secondsToMilliseconds = 1000;
    const passcodeLifetime = PASSCODE_LIFETIME_IN_MINS * secondsInMinute * secondsToMilliseconds;

    timerRef.current = setTimeout(() => {
      setCodeHasExpired(true);
    }, passcodeLifetime);
  };

  useEffect(() => {

    const cleanupTimerOnUnmount = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };

    return cleanupTimerOnUnmount;
  }, []);

  // Whenever the user switches login method,
  // re-enable the email field
  useEffect(() => {
    setDisabled(false);
  }, [ withPassword, setDisabled ]);

  // Hydrate pre-filled email from querystring
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setEmail(emailParam);
      void router.replace(
        pathname,
      );
    }
  }, [ router, setEmail ]);

  const handleToggleLoginMethod = () => {
    toggleWithPassword();
  };

  return (
    <Column
      component={Login.name}
      className="flex items-center justify-center bg-light-grey px-4 md:px-0"
    >
      <Spacer size="md" />
      <div className="h-24 w-40 md:w-72 md:h-48">
        <Image
          alt="KatKin Login"
          image={{
            src: "/images/features/login/login-cat.webp",
            width: 500,
            height: 285,
            alt: "KatKin Login",
          }}
        />
      </div>
      <Text size={{ default: "3xl", md: "4xl" }} tag="h1" display="title" align="center" >
        Welcome Back!
      </Text>
      <Spacer size="md" />
      <Text display="subtitle">
        { "Don't have an account?" }
        { " " }
        <span className="underline">
          <Link href="/sign-up">Join now!</Link>
        </span>
      </Text>
      <Spacer size="lg" />
      <Suspense>
        <Input
          value={email}
          onChange={setEmail}
          disabled={disabled}
          type="email"
          className="md:w-96 w-full"
          name="Email"
          label="Email"
          placeholder="your@email.com"
          optional={true}
        />
      </Suspense>
      <Spacer size="lg" />

      { withPassword && (
        <Suspense>
          <PasswordLogin email={email}/>
        </Suspense>
      ) }
      { !withPassword && (
        <Suspense>
          <PasscodeLogin
            email={email}
            disabled={disabled}
            setDisabled={setDisabled}
            passcodeSent={passcodeSent}
            setPasscodeSent={setPasscodeSent}
            codeHasExpired={codeHasExpired}
            startPasscodeTimer={startPasscodeTimer}/>
        </Suspense>
      ) }
      <Spacer size="lg" />
      <Column direction="row" className="w-full" align="center">
        <div className="border-2 flex-1 h-0 -ml-4 md:ml-0" />
        <button className="font-display underline text-sm mx-3" onClick={handleToggleLoginMethod}>
          or log in with { withPassword ? "a passcode" : "your password" }
        </button>
        <div className="border-2 flex-1 h-0 -mr-4 md:mr-0" />
      </Column>
      <Spacer size="2xl" />
    </Column>
  );
};
